import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  AutomationTemplate,
  CreateAutomationTemplateOptions,
  CursorQuery,
  QueryAutomationTemplatesForAppOptions,
  QueryAutomationTemplatesForAppResponse,
  QueryV2,
  ResolveAutomationTemplatesResponse,
  UpdateAutomationTemplate,
  UpdateAutomationTemplateOptions,
  createAutomationTemplate as universalCreateAutomationTemplate,
  deleteAutomationTemplate as universalDeleteAutomationTemplate,
  getAutomationTemplate as universalGetAutomationTemplate,
  queryAutomationTemplatesForApp as universalQueryAutomationTemplatesForApp,
  resolveAutomationTemplates as universalResolveAutomationTemplates,
  updateAutomationTemplate as universalUpdateAutomationTemplate,
} from './automations-templates-v1-automation-template-automation-templates.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/automations' };

/** @internal */
export function createAutomationTemplate(
  httpClient: HttpClient
): CreateAutomationTemplateSignature {
  return (
    automationTemplate: NonNullablePaths<
      AutomationTemplate,
      | `automation`
      | `automation.configuration`
      | `automation.configuration.actions`
      | `automation.configuration.rootActionIds`
      | `automation.configuration.trigger`
      | `automation.configuration.trigger.appId`
      | `automation.configuration.trigger.triggerKey`
      | `description`
      | `name`,
      5
    >,
    options: NonNullablePaths<
      CreateAutomationTemplateOptions,
      `appId` | `originalMetaSiteId`,
      2
    >
  ) =>
    universalCreateAutomationTemplate(
      automationTemplate,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface CreateAutomationTemplateSignature {
  /**
   * Creates an AutomationTemplate.
   * @param - AutomationTemplate to be created.
   * @returns The created AutomationTemplate.
   */
  (
    automationTemplate: NonNullablePaths<
      AutomationTemplate,
      | `automation`
      | `automation.configuration`
      | `automation.configuration.actions`
      | `automation.configuration.rootActionIds`
      | `automation.configuration.trigger`
      | `automation.configuration.trigger.appId`
      | `automation.configuration.trigger.triggerKey`
      | `description`
      | `name`,
      5
    >,
    options: NonNullablePaths<
      CreateAutomationTemplateOptions,
      `appId` | `originalMetaSiteId`,
      2
    >
  ): Promise<
    NonNullablePaths<
      AutomationTemplate,
      | `automation.applicationInfo.appId`
      | `automation.preinstalledInfo.appId`
      | `automation.templateInfo.appId`
      | `automation.createdBy.userId`
      | `automation.createdBy.appId`
      | `automation.name`
      | `automation.configuration.status`
      | `automation.configuration.trigger.appId`
      | `automation.configuration.trigger.triggerKey`
      | `automation.configuration.trigger.filters`
      | `automation.configuration.trigger.filters.${number}._id`
      | `automation.configuration.trigger.filters.${number}.fieldKey`
      | `automation.configuration.trigger.filters.${number}.filterExpression`
      | `automation.configuration.trigger.scheduledEventOffset.preScheduledEventOffsetExpression`
      | `automation.configuration.trigger.scheduledEventOffset.scheduledEventOffsetTimeUnit`
      | `automation.configuration.trigger.rateLimit.maxActivationsExpression`
      | `automation.configuration.trigger.rateLimit.durationTimeUnit`
      | `automation.configuration.rootActionIds`
      | `automation.origin`
      | `automation.settings.hidden`
      | `automation.settings.readonly`
      | `automation.settings.disableDelete`
      | `automation.settings.disableStatusChange`
      | `automation.settings.actionSettings.permanentActionIds`
      | `automation.settings.actionSettings.readonlyActionIds`
      | `automation.archived`
      | `appId`
      | `name`
      | `description`,
      7
    >
  >;
}

/** @internal */
export function getAutomationTemplate(
  httpClient: HttpClient
): GetAutomationTemplateSignature {
  return (automationTemplateId: string) =>
    universalGetAutomationTemplate(
      automationTemplateId,
      // @ts-ignore
      { httpClient }
    );
}

interface GetAutomationTemplateSignature {
  /**
   * Retrieves an AutomationTemplate.
   * @param - ID of the AutomationTemplate to retrieve.
   * @returns The requested AutomationTemplate.
   */
  (automationTemplateId: string): Promise<
    NonNullablePaths<
      AutomationTemplate,
      | `automation.applicationInfo.appId`
      | `automation.preinstalledInfo.appId`
      | `automation.templateInfo.appId`
      | `automation.createdBy.userId`
      | `automation.createdBy.appId`
      | `automation.name`
      | `automation.configuration.status`
      | `automation.configuration.trigger.appId`
      | `automation.configuration.trigger.triggerKey`
      | `automation.configuration.trigger.filters`
      | `automation.configuration.trigger.filters.${number}._id`
      | `automation.configuration.trigger.filters.${number}.fieldKey`
      | `automation.configuration.trigger.filters.${number}.filterExpression`
      | `automation.configuration.trigger.scheduledEventOffset.preScheduledEventOffsetExpression`
      | `automation.configuration.trigger.scheduledEventOffset.scheduledEventOffsetTimeUnit`
      | `automation.configuration.trigger.rateLimit.maxActivationsExpression`
      | `automation.configuration.trigger.rateLimit.durationTimeUnit`
      | `automation.configuration.rootActionIds`
      | `automation.origin`
      | `automation.settings.hidden`
      | `automation.settings.readonly`
      | `automation.settings.disableDelete`
      | `automation.settings.disableStatusChange`
      | `automation.settings.actionSettings.permanentActionIds`
      | `automation.settings.actionSettings.readonlyActionIds`
      | `automation.archived`
      | `appId`
      | `name`
      | `description`,
      7
    >
  >;
}

/** @internal */
export function updateAutomationTemplate(
  httpClient: HttpClient
): UpdateAutomationTemplateSignature {
  return (
    _id: string,
    automationTemplate: NonNullablePaths<
      UpdateAutomationTemplate,
      `revision`,
      2
    >,
    options: NonNullablePaths<UpdateAutomationTemplateOptions, `appId`, 2>
  ) =>
    universalUpdateAutomationTemplate(
      _id,
      automationTemplate,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateAutomationTemplateSignature {
  /**
   * Updates an AutomationTemplate.
   * @param - AutomationTemplate ID.
   * @returns Updated AutomationTemplate.
   */
  (
    _id: string,
    automationTemplate: NonNullablePaths<
      UpdateAutomationTemplate,
      `revision`,
      2
    >,
    options: NonNullablePaths<UpdateAutomationTemplateOptions, `appId`, 2>
  ): Promise<
    NonNullablePaths<
      AutomationTemplate,
      | `automation.applicationInfo.appId`
      | `automation.preinstalledInfo.appId`
      | `automation.templateInfo.appId`
      | `automation.createdBy.userId`
      | `automation.createdBy.appId`
      | `automation.name`
      | `automation.configuration.status`
      | `automation.configuration.trigger.appId`
      | `automation.configuration.trigger.triggerKey`
      | `automation.configuration.trigger.filters`
      | `automation.configuration.trigger.filters.${number}._id`
      | `automation.configuration.trigger.filters.${number}.fieldKey`
      | `automation.configuration.trigger.filters.${number}.filterExpression`
      | `automation.configuration.trigger.scheduledEventOffset.preScheduledEventOffsetExpression`
      | `automation.configuration.trigger.scheduledEventOffset.scheduledEventOffsetTimeUnit`
      | `automation.configuration.trigger.rateLimit.maxActivationsExpression`
      | `automation.configuration.trigger.rateLimit.durationTimeUnit`
      | `automation.configuration.rootActionIds`
      | `automation.origin`
      | `automation.settings.hidden`
      | `automation.settings.readonly`
      | `automation.settings.disableDelete`
      | `automation.settings.disableStatusChange`
      | `automation.settings.actionSettings.permanentActionIds`
      | `automation.settings.actionSettings.readonlyActionIds`
      | `automation.archived`
      | `appId`
      | `name`
      | `description`,
      7
    >
  >;
}

/** @internal */
export function deleteAutomationTemplate(
  httpClient: HttpClient
): DeleteAutomationTemplateSignature {
  return (componentId: string, appId: string) =>
    universalDeleteAutomationTemplate(
      componentId,
      appId,
      // @ts-ignore
      { httpClient }
    );
}

interface DeleteAutomationTemplateSignature {
  /**
   * Deletes an AutomationTemplate.
   * @param - Component id of the AutomationTemplate to delete.
   * @param - App id of the AutomationTemplate to delete.
   */
  (componentId: string, appId: string): Promise<void>;
}

/** @internal */
export function resolveAutomationTemplates(
  httpClient: HttpClient
): ResolveAutomationTemplatesSignature {
  return (query: QueryV2) =>
    universalResolveAutomationTemplates(
      query,
      // @ts-ignore
      { httpClient }
    );
}

interface ResolveAutomationTemplatesSignature {
  /**
   * Returns AutomationTemplates that are installed on the site and exposed to Wix users.
   * @param - Query options.
   */
  (query: QueryV2): Promise<
    NonNullablePaths<
      ResolveAutomationTemplatesResponse,
      | `automationTemplates`
      | `automationTemplates.${number}.automation.applicationInfo.appId`
      | `automationTemplates.${number}.automation.preinstalledInfo.appId`
      | `automationTemplates.${number}.automation.templateInfo.appId`
      | `automationTemplates.${number}.automation.createdBy.userId`
      | `automationTemplates.${number}.automation.createdBy.appId`
      | `automationTemplates.${number}.automation.name`
      | `automationTemplates.${number}.automation.configuration.status`
      | `automationTemplates.${number}.automation.origin`
      | `automationTemplates.${number}.automation.settings.hidden`
      | `automationTemplates.${number}.automation.settings.readonly`
      | `automationTemplates.${number}.automation.settings.disableDelete`
      | `automationTemplates.${number}.automation.settings.disableStatusChange`
      | `automationTemplates.${number}.automation.archived`
      | `automationTemplates.${number}.appId`
      | `automationTemplates.${number}.name`
      | `automationTemplates.${number}.description`,
      6
    >
  >;
}

/** @internal */
export function queryAutomationTemplatesForApp(
  httpClient: HttpClient
): QueryAutomationTemplatesForAppSignature {
  return (
    query: CursorQuery,
    options: NonNullablePaths<QueryAutomationTemplatesForAppOptions, `appId`, 2>
  ) =>
    universalQueryAutomationTemplatesForApp(
      query,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface QueryAutomationTemplatesForAppSignature {
  /**
   * Allows an app to query its automation templates.
   * @param - Query options.
   */
  (
    query: CursorQuery,
    options: NonNullablePaths<QueryAutomationTemplatesForAppOptions, `appId`, 2>
  ): Promise<
    NonNullablePaths<
      QueryAutomationTemplatesForAppResponse,
      | `automationTemplates`
      | `automationTemplates.${number}.automation.applicationInfo.appId`
      | `automationTemplates.${number}.automation.preinstalledInfo.appId`
      | `automationTemplates.${number}.automation.templateInfo.appId`
      | `automationTemplates.${number}.automation.createdBy.userId`
      | `automationTemplates.${number}.automation.createdBy.appId`
      | `automationTemplates.${number}.automation.name`
      | `automationTemplates.${number}.automation.configuration.status`
      | `automationTemplates.${number}.automation.origin`
      | `automationTemplates.${number}.automation.settings.hidden`
      | `automationTemplates.${number}.automation.settings.readonly`
      | `automationTemplates.${number}.automation.settings.disableDelete`
      | `automationTemplates.${number}.automation.settings.disableStatusChange`
      | `automationTemplates.${number}.automation.archived`
      | `automationTemplates.${number}.appId`
      | `automationTemplates.${number}.name`
      | `automationTemplates.${number}.description`,
      6
    >
  >;
}

export {
  AIMetadata,
  AccountInfo,
  Action,
  ActionEvent,
  ActionInfoOneOf,
  ActionSettings,
  AddressLocation,
  AppDefinedAction,
  ApplicationOrigin,
  AuditInfo,
  AuditInfoIdOneOf,
  AutoArchivePolicy,
  Automation,
  AutomationConfiguration,
  AutomationOriginInfoOneOf,
  AutomationSettings,
  AutomationTemplate,
  AutomationTemplateDeletedWithEntity,
  AutomationTemplateSpecInfo,
  Category,
  CategoryInfo,
  CodeConditionAction,
  CodeSnippet,
  CommonCursorPaging,
  CommonCursorPagingMetadata,
  CommonCursors,
  CommonSortOrder,
  CommonSorting,
  ConditionAction,
  ConditionExpressionGroup,
  CreateAutomationTemplateOptions,
  CreateAutomationTemplateRequest,
  CreateAutomationTemplateResponse,
  CursorPaging,
  CursorPagingMetadata,
  CursorQuery,
  CursorQueryPagingMethodOneOf,
  Cursors,
  DelayAction,
  DeleteAutomationTemplateRequest,
  DeleteAutomationTemplateResponse,
  Dimensions,
  Domain,
  DomainEvent,
  DomainEventBodyOneOf,
  DraftInfo,
  Enrichment,
  Enrichments,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  ExtendedFields,
  Filter,
  FilterValueSelection,
  FilterableAppDefinedActions,
  FutureDateActivationOffset,
  GetAutomationTemplateRequest,
  GetAutomationTemplateResponse,
  IdentificationData,
  IdentificationDataIdOneOf,
  Language,
  MergeAction,
  MessageEnvelope,
  Note,
  Notes,
  Offset,
  Operator,
  Origin,
  OutputAction,
  Paging,
  Path,
  Position,
  PreinstalledOrigin,
  QueryAutomationTemplatesForAppOptions,
  QueryAutomationTemplatesForAppRequest,
  QueryAutomationTemplatesForAppResponse,
  QueryV2,
  QueryV2PagingMethodOneOf,
  RateLimit,
  RateLimitAction,
  ResolveAutomationTemplatesRequest,
  ResolveAutomationTemplatesResponse,
  RestoreInfo,
  SegmentInfo,
  SetVariablesAction,
  SortOrder,
  Sorting,
  SplitAction,
  Status,
  TemplateOrigin,
  TimeUnit,
  Trigger,
  TriggerSettings,
  Type,
  UpdateAutomationTemplate,
  UpdateAutomationTemplateOptions,
  UpdateAutomationTemplateRequest,
  UpdateAutomationTemplateResponse,
  WebhookIdentityType,
} from './automations-templates-v1-automation-template-automation-templates.universal.js';
